import React from 'react'
import { Flex, RenderControl } from '@fareye/ui'
import { Header } from '../../components/Header'
import queryString from 'query-string'
import { cancelToken, get, responseParser, isEmpty } from '@fareye/utils'
import { IssueListing } from '@fareye/issue-resolution'
import { TripDetailContext } from '../TripDetails'
import { ModelLayout } from './ModelLayout'
import { getTimezone, useTimeZone, useTranslation } from '@fareye/common'

const initialMapState = {
  data: { issuesCount: 0 },
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: ''
}

export const MultiModelHistory = ({
  timer,
  config,
  match,
  history,
  location,
  mapKey,
  defaultBaseLayer,
  aerisLayer
}: any) => {
  const {
    styling: { layoutStyle, style, className },
    config: {
      summaryURL,
      otherDetailsURL,
      type,
      issueRaisedConfig,
      showTimezone = true,
      moveMarker = false
    }
  } = config
  const source = cancelToken().source()
  const searchParams = queryString.parse(location.search)
  const { slug }: any = match.params
  const { issueRaised } = searchParams
  const { state } = useTimeZone()
  const [showDrawer, setShowDrawer] = React.useState(issueRaised ? true : false)
  const { t } = useTranslation()
  const hasData = React.useRef(false)
  const [trackingProvider, setTrackingProvider] = React.useState('')
  const [apiResponse, setApiResponse] = React.useState(initialMapState)
  const [otherDetailsData, setOtherDetailsData] = React.useState<{
    data: any
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
    message: string
  }>({
    data: { items: [], oceanPortLogs: [], exceptions: [], stoppages: [] },
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: ''
  })

  async function fetchData() {
    if (!hasData.current) {
      setApiResponse({ ...apiResponse, isLoading: true })
    }
    try {
      const headers: any = {}
      if (config.config.isShared == 'true') {
        headers['X-Fe-Timezone'] = getTimezone()
      }
      const url = trackingProvider
        ? `${summaryURL}${slug}?tracking_provider=${trackingProvider}`
        : `${summaryURL}${slug}`
      const response = await get(url, source.token, {
        headers: {
          'Key-Inflection': 'camel',
          'X-Change-Case': true,
          ...headers
        }
      })
      const { error, data, message } = responseParser(response)
      let { result = {} } = data
      if (error) {
        setApiResponse({
          ...apiResponse,
          isError: true,
          isSuccess: false,
          isLoading: false,
          message
        })
      } else {
        const {
          packageLegs = [],
          multiVehicleHistory,
          history,
          hasMultiVehicle,
          trackingProviders = []
        } = result
        let histories: any = hasMultiVehicle ? [] : history || []
        if (!isEmpty(packageLegs)) {
          packageLegs.forEach((item: any) => {
            if (item.hasMultiVehicle) {
              let multiVehicleHistories = Object.entries(
                item.multiVehicleHistory
              ).reduce((tmp: any, [key, value]: [string, any]) => {
                tmp = [...tmp, ...value.historyRecord]
                return tmp
              }, [])
              histories = [...histories, ...multiVehicleHistories]
            } else {
              histories = [...histories, ...item.history]
            }
          })
        }
        if (!isEmpty(multiVehicleHistory)) {
          let multiVehicleHistories = Object.entries(
            multiVehicleHistory
          ).reduce((tmp: any, [key, value]: [string, any]) => {
            tmp = [...tmp, ...value.historyRecord]
            return tmp
          }, [])
          histories = [...multiVehicleHistories]
        }

        hasData.current = true
        setApiResponse({
          ...apiResponse,
          isError: false,
          data: { ...result, history: histories, oceanPortLogs: data.oceanPortLogs, trackingProviders },
          isSuccess: true,
          isLoading: false,
          message: !isEmpty(result) ? '' : message
        })
        fetchOtherDetails()
      }
    } catch (er:any) {
      console.error(
        'TRIP DETAILS::Fetching Package History Error:',
        er,
        er.response
      )
      const { error, data, message } = responseParser(er.response)
      if (error) {
        setApiResponse({
          ...apiResponse,
          isSuccess: false,
          isError: true,
          isLoading: false,
          message
        })
      }
    }
  }
  async function fetchOtherDetails() {
    setOtherDetailsData({
      data: { items: [], oceanPortLogs: [], exceptions: [], stoppages: [] },
      isLoading: false,
      isError: false,
      isSuccess: false,
      message: ''
    })
    try {
      const headers: any = {}
      if (config.config.isShared == 'true') {
        headers['X-Fe-Timezone'] = getTimezone()
      }
      const response = await get(
        `${otherDetailsURL.url}${slug}${otherDetailsURL.pathParam}`,
        source.token,
        {
          headers: {
            'Key-Inflection': 'camel',
            'X-Change-Case': true,
            ...headers
          }
        }
      )
      const { error, data, message } = responseParser(response)
      if (error) {
        setOtherDetailsData({
          data: { items: [], oceanPortLogs: [], exceptions: [], stoppages: [] },
          isLoading: false,
          isError: false,
          isSuccess: false,
          message: ''
        })
      } else {
        const {
          packageLegs = [],
          items = [],
          oceanPortLogs = [],
          exceptions = [],
          stoppages = []
        } = data
        let exception: any[] = [],
          stoppage: any[] = []
        if (!isEmpty(packageLegs)) {
          packageLegs.forEach((item: any) => {
            exception = [...exception, ...item.exceptions]
            stoppage = [...stoppage, ...item.stoppages]
          })
        }

        setOtherDetailsData({
          isError: false,
          data: {
            items: items,
            oceanPortLogs: oceanPortLogs,
            exceptions: !isEmpty(exceptions) ? exceptions : exception,
            stoppages: !isEmpty(stoppages)
              ? stoppages.filter((item: any) => item.duration !== 0)
              : stoppage.filter((item: any) => item.duration !== 0)
          },
          isSuccess: true,
          isLoading: false,
          message: !isEmpty(data) ? '' : message
        })
      }
    } catch (er:any) {
      console.error(
        'TRIP DETAILS::Fetching Package History Error:',
        er,
        er.response
      )
      const { error, data, message } = responseParser(er.response)
      if (error) {
        setOtherDetailsData({
          data: { items: [], oceanPortLogs: [], exceptions: [], stoppages: [] },
          isLoading: false,
          isError: false,
          isSuccess: false,
          message
        })
      }
    }
  }
  React.useEffect(() => {
    if (!hasData.current) {
      setApiResponse(initialMapState)
    }
    fetchData()
  }, [timer, trackingProvider])
  return (
    <TripDetailContext.Provider value={{ apiResponse, setApiResponse }}>
      {showDrawer && (
        <IssueListing
          showDrawer={showDrawer}
          setShowDrawer={setShowDrawer}
          resourceId={slug}
          resourceType={type === 'package' ? 'Shipment' : 'Journey'}
          baseUrl={''}
          config={issueRaisedConfig}
          updateIssueCount={() => {
            setApiResponse({
              ...apiResponse,
              data: {
                ...apiResponse.data,
                issuesCount: apiResponse.data.issuesCount + 1
              }
            })
          }}
          t={t}
        />
      )}
      <RenderControl state={apiResponse} variant={'xl'}>
        <Flex style={{ ...layoutStyle, ...style }} className={className} column>
          <Header
            slug={slug}
            match={match}
            config={config.config}
            history={history}
            location={location}
            showTimezone={showTimezone}
            setIssueClicked={setShowDrawer}
          />
          {!isEmpty(apiResponse.data) && (
            <ModelLayout
              type={type}
              mapKey={mapKey}
              aerisLayer={aerisLayer}
              defaultBaseLayer={defaultBaseLayer}
              config={config}
              data={apiResponse.data}
              otherData={otherDetailsData.data}
              match={match}
              moveMarker={moveMarker}
              history={history}
              location={location}
              onTrackingProviderChange={setTrackingProvider}
              trackingProvider={trackingProvider}
            />
          )}
        </Flex>
      </RenderControl>
    </TripDetailContext.Provider>
  )
}
